/**
 * Purchase Order tax dropdown options.
 *
 * Static, standard rate set shared across Playfield. Mirrors the same
 * fallback rates RM Inward's `BillTotalsPanel.jsx` uses (`LOCAL_TAX_RATES`)
 * — the intersection is intentional so the two flows stay consistent.
 * POs never push to Zoho, so Zoho's live tax objects are not a source
 * here. Expressed as a flat list (not a `{key: rate}` map) so the tax
 * TYPE (IGST vs intra-state GST) stays a first-class field the UI
 * dropdown can display, the PO doc can persist, and the PDF renderer
 * can read to build the "IGST18 (18%)" / "GST18 (18%)" label.
 *
 * `value` is the stable string the <Select> component tracks (must be
 * unique). `tax_type` is what gets persisted onto the PO — `null` for
 * NONE, the string label ("IGST"/"GST") otherwise. `rate` is the
 * number persisted into `tax_rate` and consumed by the totals math.
 */
export const PO_TAX_OPTIONS = [
  { value: "NONE",    tax_type: null,   rate: 0,  label: "None (0%)" },
  { value: "IGST_5",  tax_type: "IGST", rate: 5,  label: "IGST 5%" },
  { value: "IGST_12", tax_type: "IGST", rate: 12, label: "IGST 12%" },
  { value: "IGST_18", tax_type: "IGST", rate: 18, label: "IGST 18%" },
  { value: "IGST_28", tax_type: "IGST", rate: 28, label: "IGST 28%" },
  { value: "GST_5",   tax_type: "GST",  rate: 5,  label: "GST 5%" },
  { value: "GST_12",  tax_type: "GST",  rate: 12, label: "GST 12%" },
  { value: "GST_18",  tax_type: "GST",  rate: 18, label: "GST 18%" },
  { value: "GST_28",  tax_type: "GST",  rate: 28, label: "GST 28%" },
];

/** Best-effort resolution of a persisted (tax_type, tax_rate) pair
 *  back to the matching option `value`. Legacy POs written before
 *  `tax_type` existed fall back to "IGST" per the PDF renderer's
 *  backward-compat rule. When rate is 0 (or NONE), returns "NONE".
 *  When no exact match is found, returns "NONE" so the dropdown never
 *  shows a phantom / invalid selection. */
export function resolveTaxOptionValue(taxType, taxRate) {
  const rate = Number(taxRate ?? 0);
  if (!rate) return "NONE";
  const type = taxType || "IGST";
  const hit = PO_TAX_OPTIONS.find(
    (o) => o.tax_type === type && o.rate === rate,
  );
  return hit ? hit.value : "NONE";
}
